'use client';

import { useEffect, useCallback, useState, useRef, useContext } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import Cookies from 'js-cookie';
import { FlagshipContext } from '@flagship.io/react-sdk';
import { useRequestQueue } from '@web/queue/context';
import { ApiResult, useService } from '../api';
import { useCampaign } from '../campaign';
import { useExperiment } from '../experiments/useExperiment';
import {
  CHECKOUT_NEXT_BOX_PRICE,
  CHECKOUT_GWP_CALLOUT,
  SHOW_CHECKOUT_BANNERS,
  CHECKOUT_VALUE_PROPS_LOCATION,
  CHECKOUT_CX_CTAS,
  SKIP_GWP_CODES,
  SHOW_GIFT_CHECKOUT_EXTENSION,
} from '../experiments/constants';
import { shared } from '../config';
import {
  SAMPLER_OFFER_CODE_COOKIE,
  STARTER_OFFER_CODE_COOKIE,
  resolveStarterOfferCodeToApply,
  shouldApplyOfferToStarterCart,
  shouldHydrateExistingStarterCart,
} from '../offer/samplerOffer';
import { ShopifyCartService } from './services';
import { getABTestFlags, resolveAllowFreeGiftSelection } from './helpers';

const getCartPayload = response => response?.data ?? response ?? null;

export const useShopifyCart = ({
  shouldCreateCart = true,
  hasCheckedPath = false,
  pathname = '',
} = {}) => {
  const service = useService(ShopifyCartService);
  const { mutate } = useSWRConfig();
  const { queueRequest } = useRequestQueue();
  const { campaignId } = useCampaign();
  const { state: flagshipState } = useContext(FlagshipContext);
  const [cartId, setCartId] = useState(null);
  const [isFetchingOfferFlags, setIsFetchingOfferFlags] = useState(true);
  const creatingRef = useRef(null);
  const cartIdRef = useRef(cartId);
  const flagsFetchedForRef = useRef(null);

  const { value: showCheckoutNextBoxPrice } = useExperiment(
    CHECKOUT_NEXT_BOX_PRICE,
    false,
  );
  const { value: showCheckoutGWPCallout } = useExperiment(
    CHECKOUT_GWP_CALLOUT,
    false,
  );
  const { value: showCheckoutBanners } = useExperiment(
    SHOW_CHECKOUT_BANNERS,
    true,
  );
  const { value: checkoutValuePropsLocation } = useExperiment(
    CHECKOUT_VALUE_PROPS_LOCATION,
    'control',
  );
  const { value: checkoutCxCtas } = useExperiment(CHECKOUT_CX_CTAS, 'control');
  const { value: skipGwpCodes } = useExperiment(SKIP_GWP_CODES, []);
  const { value: showGiftCheckoutExtension } = useExperiment(
    SHOW_GIFT_CHECKOUT_EXTENSION,
    false,
  );

  // Keep cartIdRef.current updated with the latest cartId
  cartIdRef.current = cartId;

  const updateCartId = useCallback(
    newId => {
      if (newId && newId !== cartId) {
        setCartId(newId);
        Cookies.set('shopifyCartId', newId, { path: '/', expires: 30 });
      }
    },
    [cartId],
  );

  const applyStoredOfferCode = useCallback(
    async (newCartId, existingCart = null) => {
      const defaultCode = shared.DEFAULT_OFFER_CODE || 'DEFAULT';
      const applyUrlOffer = shouldApplyOfferToStarterCart(
        window.location.pathname,
      );
      const cartOfferCode = existingCart?.coupon_code?.[0] || null;
      const codeToApply = resolveStarterOfferCodeToApply({
        urlOfferCode: applyUrlOffer
          ? new URLSearchParams(window.location.search).get('offer_code')
          : null,
        storedOfferCode: Cookies.get(STARTER_OFFER_CODE_COOKIE),
        samplerOfferCode: Cookies.get(SAMPLER_OFFER_CODE_COOKIE),
        defaultOfferCode: defaultCode,
        cartOfferCode,
      });

      if (
        cartOfferCode &&
        codeToApply &&
        cartOfferCode.toLowerCase() === codeToApply.toLowerCase()
      ) {
        return existingCart;
      }

      const applyDefaultPromo = async () => {
        Cookies.set(STARTER_OFFER_CODE_COOKIE, defaultCode, { path: '/' });
        try {
          const response = await service.addPromo(newCartId, defaultCode);
          return getCartPayload(response);
        } catch {
          // Promo failure must not block cart initialization.
          return null;
        }
      };

      if (!codeToApply || codeToApply === defaultCode) {
        // No starter offer — apply DEFAULT as baseline so the cart is never
        // empty. Also replaces a leftover sampler code so checkout keeps a
        // valid onboarding discount.
        return applyDefaultPromo();
      }
      try {
        await service.validatePromo(codeToApply);
      } catch (e) {
        return applyDefaultPromo();
      }
      try {
        const response = await service.addPromo(newCartId, codeToApply);
        return getCartPayload(response);
      } catch (e) {
        return null;
      }
    },
    [service],
  );

  const createCart = useCallback(async () => {
    if (cartIdRef.current) return { cart_id: cartIdRef.current };

    //* If a creation is already in progress, return the existing promise
    if (creatingRef.current) {
      return creatingRef.current;
    }

    creatingRef.current = (async () => {
      try {
        const response = await service.createCart();

        if (response?.data?.cart_id) {
          // Apply the stored offer code BEFORE setting cartId. Otherwise
          // updateCartId triggers the SWR fetch immediately and the cart
          // data lands in state without the coupon, which causes the
          // pages-router ShopifyOfferProvider to then apply DEFAULT over
          // the top. Applying first means the SWR fetch sees the cart
          // already couponed.
          try {
            await applyStoredOfferCode(response.data.cart_id);
          } finally {
            updateCartId(response.data.cart_id);
          }
          return response.data;
        } else {
          return response?.data || null;
        }
      } catch (error) {
        creatingRef.current = null;
        return null;
      }
    })();

    return creatingRef.current;
  }, [service, updateCartId, applyStoredOfferCode]);

  const flags = {
    showCheckoutNextBoxPrice,
    showCheckoutGWPCallout,
    showCheckoutBanners,
    checkoutValuePropsLocation,
    checkoutCxCtas,
  };

  const {
    data: cartData,
    error: cartError,
    isLoading: isShopifyCartLoading,
    mutate: refreshCart,
  } = useSWR(cartId ? `/api/v4/onboarding/${cartId}/` : null, null, {
    revalidateOnFocus: false,
    dedupingInterval: 10000,
    onSuccess: async data => {
      const showFreeGiftSelectionExtension =
        showGiftCheckoutExtension &&
        resolveAllowFreeGiftSelection(data.coupon_code, skipGwpCodes);

      const attributes = {
        order_type: 'onboarding',
        cart_id: data.cart_id,
        ab_tests: getABTestFlags({ ...flags, showFreeGiftSelectionExtension }),
      };

      // Segment's anonymousId doubles as customer_id in cart attributes so
      // Shopify-side analytics/events and customer creation/order hydration
      // can tie an anonymous cart back to its Segment identity.
      if (!data.customer) {
        let anonymousId = '';
        if (window.analytics && window.analytics.user) {
          anonymousId = window.analytics.user().anonymousId();
        }
        attributes.customer_id = anonymousId;
      }

      queueRequest(() => updateCartAttributes(attributes, data.cart_id));
    },
    onError: async error => {
      if (error.response?.status === 404) {
        Cookies.remove('shopifyCartId', { path: '/' });
        setCartId(null);
        if (shouldCreateCart) {
          await createCart();
        }
      }
    },
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      if (retryCount >= 1) return;
      setTimeout(() => revalidate({ retryCount }), 1000);
    },
  });

  const syncRecart = useCallback(
    id => {
      const sessionId = window._recart?.getSessionId?.();
      if (!sessionId || !id) return;
      service.syncRecartCart(sessionId, id).catch(err =>
        // eslint-disable-next-line no-console
        console.warn('Recart sync failed:', err),
      );
    },
    [service],
  );

  const addPetData = useCallback(
    async (petData = {}) => {
      let currentCartId = cartIdRef.current; // potential race condition handling with ref

      // If no cartId, try to create one
      if (!currentCartId) {
        const newCart = await createCart();
        currentCartId = newCart?.cart_id;
      }

      if (!currentCartId) {
        return null;
      }

      try {
        const response = await service.addPetData(currentCartId, {
          ...petData,
        });

        if (!currentCartId) {
          return null;
        }

        await refreshCart(response.data, false);
        return response?.data;
      } catch (error) {
        throw error;
      }
    },
    [service, refreshCart, createCart],
  );

  const deletePetFromCart = useCallback(
    async petId => {
      if (!cartId) return null;

      const response = await service.removePet(cartId, petId);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, refreshCart],
  );

  const addPromo = useCallback(
    async promoCode => {
      if (!cartId || cartError) return null;

      const response = await service.addPromo(cartId, promoCode);
      await refreshCart(response.data, false);
      return response.data;
    },
    [service, cartId, cartError, refreshCart],
  );

  const upsertMealPlan = useCallback(
    async (petId, planData = {}) => {
      if (!cartId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.upsertMealPlan(cartId, petId, planData);
        await refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachExtras = useCallback(
    async (petId, extrasData = []) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachExtras(cartId, petId, extrasData);
        refreshCart(response.data, false);
        syncRecart(cartId);
        return response.data;
      });
    },
    [service, cartId, refreshCart, syncRecart],
  );

  const attachFreeGifts = useCallback(
    async (petId, giftData = {}) => {
      if (!cartId || !petId) return null;
      return ApiResult.callAsync(async () => {
        const response = await service.attachFreeGifts(cartId, petId, giftData);
        refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  const updateCartAttributes = useCallback(
    async (attributes = {}, idOverride = null) => {
      const id = idOverride || cartId;

      if (!id) {
        // eslint-disable-next-line no-console
        console.warn('updateCartAttributes: No Cart ID available');
        return null;
      }

      return ApiResult.callAsync(async () => {
        const response = await service.updateAttributes(id, attributes);
        await refreshCart(response.data, false);
        return response.data;
      });
    },
    [service, cartId, refreshCart],
  );

  useEffect(() => {
    //* I found that the createCart call being awaited was better for adding the attribute following.
    const initCart = async () => {
      const savedCartId = Cookies.get('shopifyCartId');

      if (savedCartId) {
        if (shouldCreateCart) {
          try {
            let existingCart = null;
            try {
              const response = await service.getCart(savedCartId);
              existingCart = getCartPayload(response);
            } catch {
              existingCart = null;
            }
            const cart = await applyStoredOfferCode(savedCartId, existingCart);
            if (cart) {
              await mutate(`/api/v4/onboarding/${savedCartId}/`, cart, false);
            } else if (cartIdRef.current === savedCartId) {
              await mutate(`/api/v4/onboarding/${savedCartId}/`);
            }
          } finally {
            setCartId(savedCartId);
          }
        } else if (
          shouldHydrateExistingStarterCart({
            savedCartId,
            shouldCreateCart,
            pathname: window.location.pathname,
          })
        ) {
          setCartId(savedCartId);
        }
      } else if (shouldCreateCart) {
        try {
          await createCart();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('Failed to initialize cart:', error);
        }
      }
    };

    initCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreateCart]);

  // initCart does not re-run when only the path changes. Landing on /sampler
  // skips the starter cart, and a later client navigation to / keeps
  // shouldCreateCart false, so hydrate once the path is a marketing page.
  useEffect(() => {
    if (
      !pathname ||
      shouldCreateCart ||
      cartIdRef.current ||
      pathname.includes('/onboarding')
    ) {
      return;
    }

    const savedCartId = Cookies.get('shopifyCartId');
    if (
      shouldHydrateExistingStarterCart({
        savedCartId,
        shouldCreateCart,
        pathname,
      })
    ) {
      setCartId(savedCartId);
    }
  }, [pathname, shouldCreateCart]);

  //* Update cart attributes with campaign_id & cart_id when cart is first created
  useEffect(() => {
    if (!cartId || !campaignId) return;
    const updateCampaign = async campaignId => {
      queueRequest(() =>
        updateCartAttributes({
          campaign_id: campaignId,
        }),
      );
    };
    updateCampaign(campaignId);
  }, [cartId, queueRequest, updateCartAttributes, campaignId]);

  const getCouponCode = useCallback(() => {
    if (!cartData?.coupon_code?.length) return null;
    return cartData.coupon_code[0];
  }, [cartData]);

  const getAllowFreeGiftSelection = useCallback(
    () => resolveAllowFreeGiftSelection(cartData?.coupon_code, skipGwpCodes),
    [cartData, skipGwpCodes],
  );

  const showFreeGiftSelectionExtension =
    showGiftCheckoutExtension &&
    resolveAllowFreeGiftSelection(cartData?.coupon_code, skipGwpCodes);

  const updatedABTestFlags = getABTestFlags({
    ...flags,
    showFreeGiftSelectionExtension,
  });

  const hasCartData = Boolean(cartData);

  //* Keep show_free_gift_selection_extension in sync — coupon_code (and thus
  //* eligibility) can change after the cart's initial load (e.g. addPromo),
  //* so this shouldn't live in onSuccess, which only fires on the initial fetch.
  useEffect(() => {
    if (!cartId || !hasCartData) return;
    queueRequest(() =>
      updateCartAttributes({
        ab_tests: updatedABTestFlags,
      }),
    );
  }, [
    cartId,
    hasCartData,
    updatedABTestFlags,
    queueRequest,
    updateCartAttributes,
  ]);

  // Sync the offerCode cookie and Flagship visitor context with
  // the cart's current coupon code whenever it changes.
  useEffect(() => {
    if (!cartData || !cartId) return;

    // Sampler Flagship/cookie sync lives in useSamplerCart. The starter cart is
    // still mounted on /sampler and /sampler-box and would otherwise apply the
    // sampler URL code to a leftover shopifyCartId.
    if (!shouldApplyOfferToStarterCart(window.location.pathname)) {
      flagsFetchedForRef.current = null;
      return;
    }

    const cartCouponCode = cartData?.coupon_code?.[0] ?? '';
    const cookieOfferCode = Cookies.get(STARTER_OFFER_CODE_COOKIE) ?? '';

    // bail out if the URL carries an offer_code that hasn't been applied to the cart yet
    if (typeof window !== 'undefined') {
      const urlOfferCode =
        new URLSearchParams(window.location.search).get('offer_code') ?? '';
      if (urlOfferCode && urlOfferCode !== cartCouponCode) return;
    }

    const visitor = flagshipState?.visitor;

    if (
      visitor &&
      cartCouponCode &&
      flagsFetchedForRef.current !== cartCouponCode
    ) {
      flagsFetchedForRef.current = cartCouponCode;
      setIsFetchingOfferFlags(true);
      visitor.updateContext({ offerCode: cartCouponCode?.toLowerCase() });
      visitor.fetchFlags().finally(() => setIsFetchingOfferFlags(false));
    } else {
      setIsFetchingOfferFlags(false);
    }

    if (cartCouponCode === cookieOfferCode) return;

    if (cartCouponCode) {
      Cookies.set(STARTER_OFFER_CODE_COOKIE, cartCouponCode, { path: '/' });
    } else {
      Cookies.remove(STARTER_OFFER_CODE_COOKIE, { path: '/' });
    }
  }, [cartData, cartId, flagshipState?.visitor, shouldCreateCart]);

  const isLoading =
    !hasCheckedPath ||
    isShopifyCartLoading ||
    (shouldCreateCart && !cartData && !cartError);

  return {
    cartId,
    cart: cartData || {},
    error: cartError,
    isLoading,
    isFetchingOfferFlags,
    createCart,
    addPetData,
    deletePetFromCart,
    addPromo,
    upsertMealPlan,
    attachExtras,
    attachFreeGifts,
    refreshCart,
    updateCartAttributes,
    couponCode: getCouponCode(),
    allowFreeGiftSelection: getAllowFreeGiftSelection(),
  };
};
